import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getMessages, listChatRooms, openDM, sendMessage } from "@/api/chat";
import { searchUsers } from "@/api/users";
import { chatSocket } from "@/api/chat-socket";
import type { MessageRead } from "@/api/community-types";
import { useAuth } from "@/auth/auth-context";
import { Button, Input, Spinner, cx } from "@/components/ui";
import { Avatar } from "@/components/avatar";
import { useTranslation } from "react-i18next";

export function ChatPane() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const myId = user?.id;

  const roomsQuery = useQuery({ queryKey: ["chat-rooms"], queryFn: listChatRooms });
  const [activeRoom, setActiveRoom] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageRead[]>([]);
  const [draft, setDraft] = useState("");
  const activeRef = useRef<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Dedupe-on-id append (WS fanout echoes our own sent message back).
  const appendMessage = (m: MessageRead) =>
    setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));

  // Connect the socket for the lifetime of the chat pane.
  useEffect(() => {
    chatSocket.connect();
    const off = chatSocket.onMessage((m) => {
      if (m.room_id === activeRef.current) appendMessage(m);
      qc.invalidateQueries({ queryKey: ["chat-rooms"] });
    });
    return () => {
      off();
      chatSocket.disconnect();
    };
  }, [qc]);

  // Load history + (re)subscribe when the active room changes.
  useEffect(() => {
    const prev = activeRef.current;
    activeRef.current = activeRoom;
    if (prev && prev !== activeRoom) chatSocket.unsubscribe(prev);
    if (!activeRoom) {
      setMessages([]);
      return;
    }
    chatSocket.subscribe(activeRoom);
    let cancelled = false;
    getMessages(activeRoom)
      .then((rows) => {
        if (!cancelled) setMessages([...rows].reverse());
      })
      .catch(() => {
        if (!cancelled) setMessages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeRoom]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !activeRoom) return;
    setDraft("");
    try {
      const m = await sendMessage(activeRoom, body);
      appendMessage(m);
    } catch {
      setDraft(body); // restore on failure
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
      {/* Rooms + new DM */}
      <div className="space-y-3">
        <NewDM
          onOpened={(roomId) => {
            qc.invalidateQueries({ queryKey: ["chat-rooms"] });
            setActiveRoom(roomId);
          }}
        />
        <div className="card divide-y divide-black/5 overflow-hidden">
          {roomsQuery.isLoading && (
            <div className="flex justify-center py-8 text-brand-600">
              <Spinner className="h-5 w-5" />
            </div>
          )}
          {roomsQuery.data?.length === 0 && (
            <p className="p-4 text-sm text-ink-faint">{t("No conversations yet. Start a DM above.")}</p>
          )}
          {roomsQuery.data?.map((r) => (
            <button
              key={r.id}
              onClick={() => setActiveRoom(r.id)}
              className={cx(
                "flex w-full items-center gap-3 px-4 py-3 text-left transition",
                activeRoom === r.id ? "bg-brand-50" : "hover:bg-black/[0.03]",
              )}
            >
              {r.room_kind === "clan" ? (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm">
                  👥
                </span>
              ) : (
                <Avatar url={r.avatar_url} name={r.title} size={36} />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-ink">{r.title}</span>
                {r.last_message_preview && (
                  <span className="block truncate text-xs text-ink-faint">{r.last_message_preview}</span>
                )}
              </span>
              {r.unread_count > 0 && (
                <span className="rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {r.unread_count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Active room */}
      <div className="card flex h-[28rem] flex-col">
        {!activeRoom ? (
          <div className="flex flex-1 items-center justify-center text-sm text-ink-faint">
            {t("Select a conversation")}
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-2 overflow-y-auto p-4">
              {messages.map((m) => {
                const mine = m.sender_user_id === myId;
                return (
                  <div key={m.id} className={cx("flex items-end gap-2", mine ? "justify-end" : "justify-start")}>
                    {!mine && <Avatar url={m.sender_avatar_url} name={m.sender_display_name} size={24} />}
                    <div
                      className={cx(
                        "max-w-[75%] rounded-2xl px-3.5 py-2 text-sm",
                        mine ? "bg-brand-600 text-white" : "bg-black/5 text-ink",
                      )}
                    >
                      {!mine && (
                        <span className="mb-0.5 block text-xs font-semibold text-brand-600">
                          {m.sender_display_name}
                        </span>
                      )}
                      <span className="whitespace-pre-wrap">{m.body}</span>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={send} className="flex gap-2 border-t border-black/5 p-3">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t("Type a message…")}
                className="flex-1 rounded-xl border border-black/10 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
              <Button type="submit" disabled={!draft.trim()}>
                {t("Send")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function NewDM({ onOpened }: { onOpened: (roomId: string) => void }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [submittedQ, setSubmittedQ] = useState("");
  const [error, setError] = useState<string | null>(null);

  const searchQuery = useQuery({
    queryKey: ["user-search", submittedQ],
    queryFn: () => searchUsers(submittedQ),
    enabled: submittedQ.length >= 2,
  });

  const start = async (userId: string) => {
    setError(null);
    try {
      const room = await openDM(userId);
      setQ("");
      setSubmittedQ("");
      onOpened(room.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Couldn't open DM."));
    }
  };

  return (
    <div className="card p-3">
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("New message to…")}
          className="text-sm"
        />
        <Button size="sm" onClick={() => setSubmittedQ(q.trim())} disabled={q.trim().length < 2}>
          {t("Find")}
        </Button>
      </div>
      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
      {searchQuery.data && searchQuery.data.length > 0 && (
        <div className="mt-2 space-y-1">
          {searchQuery.data.map((u) => (
            <button
              key={u.user_id}
              onClick={() => start(u.user_id)}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-black/5"
            >
              <span className="truncate text-ink">{u.display_name}</span>
              {u.username && <span className="text-xs text-ink-faint">@{u.username}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
